/** Request list with filters. */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { RequestResponse } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { apiGetPage } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import { PageHeader } from "../../components/ui/PageHeader.js";
import { StatusBadge } from "../../components/StatusBadge.js";
import {
  CheckboxField,
  DataList,
  FilterTabs,
  SearchBox,
  useUrlFilters,
} from "../../components/ui/controls.js";
import { ErrorPanel, LoadingPanel, formatDate } from "../shared.js";

export function RequestsListPage() {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const { values, page, set } = useUrlFilters([
    "scope",
    "q",
    "mine",
    "status",
  ] as const);
  const scope = (values.scope || "open") as "open" | "closed" | "all";
  const pageSize = 25;
  const canViewAll = has("requests.view_all");
  const query = useQuery({
    queryKey: ["requests", values, page],
    queryFn: () =>
      apiGetPage<RequestResponse>("/requests", {
        scope: values.status ? "all" : scope,
        status: values.status || undefined,
        q: values.q || undefined,
        mine: values.mine || undefined,
        page,
        pageSize,
      }),
    placeholderData: (previous) => previous,
  });
  return (
    <>
      <PageHeader
        title={t("request.plural")}
        subtitle={t("request.listSubtitle")}
        actions={
          has("requests.create") ? (
            <Button as={Link} to="/requests/new" icon="pi pi-plus">
              {t("request.new")}
            </Button>
          ) : null
        }
      />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <FilterTabs
            value={values.status ? ("" as const) : scope}
            onChange={(next) => set({ scope: next, status: "" })}
            options={[
              { value: "open", label: t("request.scope.open") },
              { value: "closed", label: t("request.scope.closed") },
              { value: "all", label: t("common.all") },
            ]}
          />
          {canViewAll ? (
            <CheckboxField
              id="requests-mine"
              checked={values.mine === "true"}
              onChange={(mine) => set({ mine: mine ? "true" : "" })}
              label={t("request.onlyMine")}
            />
          ) : null}
        </div>
        <SearchBox value={values.q} onChange={(q) => set({ q })} />
      </div>
      {values.status ? (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <StatusBadge status={values.status} />
          <Button
            size="small"
            variant="text"
            onClick={() => set({ status: "" })}
          >
            {t("common.clearFilter")}
          </Button>
        </div>
      ) : null}
      {query.error ? (
        <ErrorPanel error={query.error} />
      ) : !query.data ? (
        <LoadingPanel />
      ) : (
        <DataList
          rows={query.data.data}
          rowKey={(request) => request.id}
          href={(request) => `/requests/${request.id}`}
          page={page}
          pageSize={pageSize}
          total={query.data.total}
          onPage={(next) => set({ page: next })}
          loading={query.isFetching || undefined}
          empty={values.q ? t("common.noMatches") : t("request.empty")}
          columns={[
            {
              key: "title",
              header: t("request.whatShort"),
              cell: (request) => request.shortDescription,
            },
            {
              key: "customer",
              header: t("common.customer"),
              cell: (request) => request.customer.displayName,
            },
            {
              key: "site",
              header: t("request.site"),
              cell: (request) => request.siteAddress?.city ?? "—",
              hideOnMobile: true,
            },
            {
              key: "when",
              header: t("request.when"),
              cell: (request) =>
                request.requestedDate
                  ? formatDate(request.requestedDate)
                  : t("request.asap"),
              hideOnMobile: true,
            },
            {
              key: "priority",
              header: t("request.priority"),
              cell: (request) =>
                request.priority === "normal" ? (
                  ""
                ) : (
                  <StatusBadge status={request.priority} />
                ),
            },
            {
              key: "status",
              header: t("common.status"),
              cell: (request) => <StatusBadge status={request.status} />,
            },
          ]}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Form                                                                */
/* ------------------------------------------------------------------ */
